import React from "react";
import { CircleAlert, CircleCheckBig, Info } from "lucide-react";
import ContentsContainer from "../../components/ContentsContainer";

const sections = [
{
    sectionId: "purpose",
    sectionTitle: "1. Purpose",
    searchKeywords: `
      grievance raise concern employment working conditions
      heard investigated resolved fairly policy
      disagreement decision treatment rights breach
      workplace issue informal harassment gender-based
      sexual complaint committee decorum investigation
      CODI investigating body step three
    `,
    sectionBody: (
      <>
        <div className="space-y-4 text-sm leading-relaxed">
          <p>
            Everyone at Hyvid has the right to raise a concern about their employment or working conditions and to have that concern heard, investigated, and resolved fairly. This policy sets out how you do that — and what Hyvid commits to in return.
          </p>

          <p>
            A grievance can be any work-related concern: a disagreement with a decision affecting your employment, a concern about how you have been treated, a perceived breach of your rights, or any other issue that affects your work that you cannot resolve informally on your own.
          </p>

          <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              This single process applies to all workplace complaints, including complaints of harassment and gender-based sexual harassment. For harassment complaints, the Committee on Decorum and Investigation (CODI) steps in as the investigating body at Step 3 — everything else follows the same process.
            </div>
          </div>
        </div>
      </>
    ),
  },

  {
    sectionId: "principles",
    sectionTitle: "2. Our Principles",
    searchKeywords: `
      fairness grievance merits
      timeliness response resolution timeframes
      confidentiality information disclosure
      retaliation good faith protection
      process parties opportunity decision
      impartiality conflict interest declare recuse
    `,
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <div className="grid sm:grid-cols-2 gap-3">

          {[
            {
              title: "Fairness",
              desc: "Every grievance will be taken seriously and assessed on its merits.",
              color: "from-emerald-400 to-green-400",
            },
            {
              title: "Timeliness",
              desc: "We will respond and resolve within the timeframes set out in this policy.",
              color: "from-blue-400 to-cyan-400",
            },
            {
              title: "Confidentiality",
              desc: "Information about your grievance will only be shared with those who need to know.",
              color: "from-purple-400 to-violet-400",
            },
            {
              title: "No retaliation",
              desc: "Raising a grievance in good faith will never be used against you.",
              color: "from-amber-400 to-orange-400",
            },
            {
              title: "Due process",
              desc: "All parties will have the opportunity to be heard before any decision is made.",
              color: "from-teal-400 to-emerald-400",
            },
            {
              title: "Impartiality",
              desc: "Anyone with a conflict of interest in a complaint must declare it and step aside.",
              color: "from-pink-400 to-rose-400",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="flex items-start gap-3 p-3 rounded-lg dark-mode-card border border-gray-100 shadow-sm"
            >
              <div className={`w-2 min-h-6 rounded-full bg-gradient-to-b ${item.color} flex-shrink-0 mt-0.5`} />
              <div>
                <span className="font-semibold title-text text-sm">{item.title}</span>
                <p className="text-xs muted-text mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}

        </div>
      </div>
    ),
  },
  {
  sectionId: "coverage",
  sectionTitle: "3. What This Policy Covers",
  searchKeywords: `
    concerns treatment manager colleague client
    pay benefits working conditions employment status
    discrimination bullying unfair harassment
    gender-based sexual breach policy agreement
    work-related matter inappropriate handling
  `,
  sectionBody: (
    <>
      <div className="space-y-4 text-sm  leading-relaxed">
        <p>You can use this process to raise concerns about:</p>

        <ul className="list-none space-y-1.5 pl-2">
          {[
            "How you have been treated by a manager, colleague, or client.",
            "A decision about your pay, benefits, working conditions, or employment status.",
            "Perceived discrimination, bullying, or unfair treatment.",
            "Harassment or gender-based sexual harassment.",
            "Breach of a Hyvid policy or your employment agreement.",
            "Any other work-related matter you believe has not been dealt with appropriately.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  ),
},
{
  sectionId: "process",
  sectionTitle: "4. The Grievance Process",
  searchKeywords: `
    informal resolution preferred workplace issues
    honest conversation manager direct fastest
    harassment complainant consent formal serious CODI
    written complaint HyvidOne description dates events
    names outcome evidence documentation supporting
    general grievances people culture
    secretariat immediately forward acknowledgement two
    working days investigation decision
    complainant respondent witnesses account evidence
    corrective action policy change arrangements
    substantiated reasons harassment gender-based sexual
    committee decorum investigation Safe Spaces Act
    RA11313 constituted woman majority members
    confidentiality penalties disciplinary consequences
    legal obligation investigate regardless formally
    timeframes appeal retaliation records identical
    unsatisfied outcome director CEO appeal
    five days incorrect unfair final
    internal process DOLE NLRC external remedy
  `,
  sectionBody: (
    <>
      <div className="space-y-4 text-sm  leading-relaxed">

        <div className="pt-2 space-y-6">

          {[
            {
              step: "1",
              title: "Step 1 — Informal Resolution (preferred first step)",
              content: (
                <>
                  <p>
                    Most workplace issues can be resolved through an honest conversation. If you have a concern, try raising it directly with your manager first. This is the fastest path to resolution and is encouraged where you feel comfortable doing so.
                  </p>

                  <p>
                    If your concern involves your manager, or you do not feel comfortable raising it directly, go straight to Step 2.
                  </p>

                  <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20  dark:border-amber-700">
                    <CircleAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold mb-1 text-amber-800 dark:text-amber-200">
                        Note for harassment complaints
                      </p>
                      Informal resolution is only appropriate for harassment complaints if the complainant genuinely prefers it and both parties consent. It is never a substitute for a formal process where the conduct is serious. The CODI will assess this at Step 3.
                    </div>
                  </div>
                </>
              ),
            },

            {
              step: "2",
              title: "Step 2 — Formal Written Complaint",
              content: (
                <>
                  <p>
                    If informal discussion does not resolve your concern, or is not appropriate, submit a formal written complaint via HyvidOne. Your complaint should include:
                  </p>

                  <ul className="list-none space-y-1.5 pl-2">
                    {[
                      "A clear description of the issue and relevant dates or events.",
                      "The names of anyone involved.",
                      "What outcome you are seeking.",
                      "Any supporting evidence or documentation you wish to provide.",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <p>General grievances are submitted to <strong>People &amp; Culture</strong>.</p>
                  <p>
                    Harassment complaints are submitted to the <strong>CODI Secretariat</strong> or People &amp; Culture, who will immediately forward them to the CODI. CODI contact details are available in HyvidOne.
                  </p>

                  <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700">
                    <CircleCheckBig className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      You will receive a written acknowledgement within <strong>two (2) working days</strong> of your complaint being received.
                    </div>
                  </div>
                </>
              ),
            },

            {
              step: "3",
              title: "Step 3 — Investigation and Decision",
              content: (
                <>
                  <p>
                    People &amp; Culture will review the complaint and determine whether a formal investigation is needed. Where an investigation proceeds, both the complainant and any respondent will have the opportunity to present their account, witnesses and evidence will be considered, and a written decision will be issued within the timeframes below.
                  </p>

                  <p>
                    The outcome may include corrective action, a policy change, a change to working arrangements, or a finding that the complaint could not be substantiated — along with clear reasons.
                  </p>

                  <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20  dark:border-amber-700">
                    <CircleAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold mb-1 text-amber-800 dark:text-amber-200">
                        Harassment complaints — CODI takes the lead
                      </p>

                      <p>
                        Where a complaint involves harassment or gender-based sexual harassment, the CODI conducts the investigation in place of People &amp; Culture. The CODI is Hyvid's Committee on Decorum and Investigation, constituted as required by the Safe Spaces Act (RA 11313). It is led by a woman and has majority women members.
                      </p>

                      <p className="mt-2">
                        Two rules apply specifically to harassment complaints that do not apply to general grievances:
                      </p>

                      <ol className="list-decimal pl-5 space-y-1 mt-1">
                        <li>The CODI must investigate regardless of whether the complainant wishes to proceed formally — this is a legal obligation under RA 11313.</li>
                        <li>Breaching confidentiality in a harassment investigation carries specific penalties under RA 11313, in addition to Hyvid's own disciplinary consequences.</li>
                      </ol>

                      <p className="mt-2">
                        In all other respects — timeframes, appeal rights, retaliation protection, record-keeping — the process is identical.
                      </p>
                    </div>
                  </div>
                </>
              ),
            },

            {
              step: "4",
              title: "Step 4 — Appeal",
              content: (
                <>
                  <p>
                    If you are not satisfied with the outcome, you may appeal the decision in writing to a Director or the CEO within <strong>five (5) working days</strong> of receiving the written decision. Appeals should state clearly why you believe the outcome was incorrect or unfair. The appeal decision is final within Hyvid's internal process.
                  </p>

                  <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      Nothing in this policy prevents you from filing a complaint with DOLE or the NLRC at any stage if you prefer to pursue an external remedy.
                    </div>
                  </div>
                </>
              ),
            },
          ].map((s) => (
            <div key={s.step} className="relative">
              <div className="dark-mode-card border border-gray-200 rounded-xl p-4 shadow-sm ml-2">
                <h4 className="font-bold title-text text-sm mb-2">
                  {s.title}
                </h4>
                <div className="text-sm  leading-relaxed space-y-2">
                  {s.content}
                </div>
              </div>
            </div>
          ))}

          <div>          
            <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              Timeframes
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ml-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                    <th className="px-3 py-2 text-left font-semibold">Stage</th>
                    <th className="px-3 py-2 text-left font-semibold">Target Timeframe</th>
                    <th className="px-3 py-2 text-left font-semibold">Who</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100 dark:border-gray-800 dark:bg-gray-900/10">
                    <td className="px-3 py-2 align-top">Step 1: Informal resolution</td>
                    <td className="px-3 py-2 align-top">Within 5 working days of issue arising</td>
                    <td className="px-3 py-2 align-top">Employee + Manager</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                    <td className="px-3 py-2 align-top">Step 2: Formal written complaint</td>
                    <td className="px-3 py-2 align-top">Within 10 working days if Step 1 not resolved</td>
                    <td className="px-3 py-2 align-top">Employee + P&C / CODI</td>
                  </tr>
                  <tr className="border-t border-gray-100 dark:border-gray-800 dark:bg-gray-900/10">
                    <td className="px-3 py-2 align-top">Acknowledgement</td>
                    <td className="px-3 py-2 align-top">Within 2 working days of receipt</td>
                    <td className="px-3 py-2 align-top">P&C / CODI Secretariat</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                    <td className="px-3 py-2 align-top">Step 3: Investigation and decision</td>
                    <td className="px-3 py-2 align-top">Within 15 working days of written complaint</td>
                    <td className="px-3 py-2 align-top">P&C or CODI (harassment)</td>
                  </tr>
                  <tr className="border-t border-gray-100  dark:border-gray-800 dark:bg-gray-900/10">
                    <td className="px-3 py-2 align-top">Step 4: Appeal (if required)</td>
                    <td className="px-3 py-2 align-top">Within 5 working days of receiving decision</td>
                    <td className="px-3 py-2 align-top">Employee + Director / CEO</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs p-2 text-xs text-gray-500">Timeframes may be extended where the complexity of the matter requires it. Both parties will be notified in writing if this occurs.</p>
          </div>
        </div>
      </div>
    </>
  ),
},
{
  sectionId: "interim",
  sectionTitle: "5. Interim Protective Measures",
  searchKeywords: `
    complaint safety wellbeing investigation interim
    measures party complainant respondent
    temporary separation meetings channels groups
    reporting lines assignments preventive suspension
    thirty days unpaid paid beyond serious
    imminent risk presence
    finding guilt temporary reviewed concluded
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        Where a complaint poses a risk to a party's safety or wellbeing while the investigation proceeds, People &amp; Culture (or the CODI for harassment complaints) may implement interim measures, such as:
      </p>

      <ul className="list-none space-y-1.5 pl-2">
        <li className="flex items-start gap-2">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
          <span>
            Temporary separation of the complainant and respondent from shared meetings, channels, or working groups.
          </span>
        </li>

        <li className="flex items-start gap-2">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
          <span>
            Temporary adjustment of reporting lines or work assignments.
          </span>
        </li>

        <li className="flex items-start gap-2">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
          <span>
            Preventive suspension of the respondent (without pay for up to 30 days, with pay beyond that) where their continued presence poses a serious and imminent risk.
          </span>
        </li>
      </ul>

      <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          Interim measures are not a finding of guilt. They are temporary and will be reviewed once the investigation is concluded.
        </div>
      </div>
    </div>
  ),
},

{
  sectionId: "retaliation",
  sectionTitle: "6. Protection from Retaliation",
  searchKeywords: `
    retaliation grievance good faith investigation
    supports colleague serious breach disciplinary
    experienced report immediately
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700">
        <CircleCheckBig className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          Hyvid will not tolerate any form of retaliation against an employee who raises a grievance in good faith, participates in an investigation, or supports a colleague doing so. Retaliation is itself a serious breach of this policy and will be subject to disciplinary action.
        </div>
      </div>

      <p>
        If you believe you have experienced retaliation for raising a grievance, report it to <strong>People &amp; Culture</strong> immediately.
      </p>
    </div>
  ),
},

{
  sectionId: "confidentiality",
  sectionTitle: "7. Confidentiality",
  searchKeywords: `
    grievance information confidential complaint
    complainant respondent witnesses personnel
    managing breach disciplinary
    harassment legal consequences RA11313
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        All information about a grievance — including that a complaint has been made — is confidential. This applies to the complainant, the respondent, any witnesses, and all Hyvid personnel involved in managing the complaint. Breaching confidentiality during or after a grievance process is a disciplinary matter.
      </p>

      <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20  dark:border-amber-700">
        <CircleAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          For harassment complaints, confidentiality obligations also carry legal consequences under <strong>RA 11313</strong>.
        </div>
      </div>
    </div>
  ),
},
{
  sectionId: "records",
  sectionTitle: "8. Records",
  searchKeywords: `
    formal grievances outcomes written record retention 
    5 years RA 10173 Access restricted legitimate
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        Hyvid will keep a written record of all formal grievances and their outcomes. Records are maintained confidentially in accordance with the <strong>Data Privacy Act (RA 10173)</strong> and retained for a minimum of <strong>five (5) years</strong>. Access is restricted to those with a legitimate need to know.
      </p>
    </div>
  ),
},

{
  sectionId: "external",
  sectionTitle: "9. External Avenues",
  searchKeywords: `
    DOLE labour standards enforcement inspections
    employer compliance RA11313 harassment
    NLRC illegal dismissal constructive labour disputes
    National Conciliation Mediation Board NCMB
    voluntary conciliation external channels
    internal process waive rights
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>At any time, you have the right to raise a concern directly with:</p>

      <div className="space-y-2 mt-1">
        {[
          {
            label: "DOLE",
            desc: "For labour standards enforcement. DOLE also conducts annual inspections of employer compliance with RA 11313 for harassment complaints.",
          },
          {
            label: "NLRC",
            desc: "For illegal dismissal, constructive dismissal, or other labour disputes.",
          },
          {
            label: "National Conciliation and Mediation Board (NCMB)",
            desc: "For voluntary conciliation.",
          },
        ].map((item) => (
          <div
            key={item.label}
            className="flex items-start gap-3 p-3 rounded-lg dark-mode-card border border-gray-100"
          >
            <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-200 text-xs font-bold whitespace-nowrap">
              {item.label}
            </span>
            <p className="text-sm muted-text">{item.desc}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          Using Hyvid's internal grievance process does not waive your right to use these external channels.
        </div>
      </div>
    </div>
  ),
},

{
  sectionId: "questions",
  sectionTitle: "10. Questions",
  searchKeywords: `
    questions process concern People Culture
    HyvidOne harassment CODI confidential
    Philippine legal counsel review
    Labor Code Philippines RA11313
    Safe Spaces Act RA7877
    Anti-Sexual Harassment Act DOLE
    DO237-22 RA10173 Data Privacy Act
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>
          If you have any questions about this process or are unsure how to raise a concern, contact <strong>People &amp; Culture</strong> via HyvidOne. For harassment complaints, you can also contact the CODI directly — details are in HyvidOne. Everything discussed will be treated confidentially.
        </p>
      </div>
      <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800/20 rounded-xl border border-gray-200 dark:border-gray-700">
        <p className="text-xs muted-text italic">
          <strong>Note:</strong> This policy should be reviewed by Philippine legal counsel before use. Legal references: Labor Code of the Philippines, RA 11313 (Safe Spaces Act), RA 7877 (Anti-Sexual Harassment Act), DOLE DO 237-22, RA 10173 (Data Privacy Act).
        </p>
      </div>
    </div>
  ),
},

];

const GrievanceDisputeContents = ({ searchTerm }) => {
  const filteredSections = sections.filter((section) => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    
    return (
      section.sectionTitle.toLowerCase().includes(search) ||
      section.searchKeywords?.toLowerCase().includes(search)
    );
  });
  return (
    <ContentsContainer sections={filteredSections} />
  );
};

export default GrievanceDisputeContents;